import axios from 'axios'
import {useUserStore} from '@/stores/user'
import event from '@/plugins/events'

// Flag to prevent multiple simultaneous refresh attempts
let isRefreshing = false
let failedQueue = []

const normalizeApiError = (error) => {
  if (!error) return new Error('Request failed')

  const isAxiosError = !!error.isAxiosError || !!error.response
  const data = isAxiosError ? error.response?.data : error
  const messageFromData = (() => {
    if (typeof data?.message === 'string') return data.message
    if (typeof data?.error === 'string') return data.error
    if (typeof data?.message === 'object') return data.message?.error || JSON.stringify(data.message)
    if (typeof data === 'string') return data
    return null
  })()

  const message = messageFromData || error.message || 'Request failed'
  const status = data?.statusCode || data?.status || error.response?.status

  // Build a new object so we never mutate AxiosError (read-only isAxiosError, etc.)
  const normalized = Object.assign(new Error(message), {
    status,
    statusCode: status,
    code: data?.code || error.code,
    error: data?.error || error.error,
    details: data?.details,
    data,
    response: error.response,
    isAxiosError
  })
  if (error.stack) normalized.stack = error.stack

  return normalized
}

export const api = axios.create({
  baseURL: '/api'
})

// Request interceptor to ensure authorization header is set
api.interceptors.request.use(function (config) {
  const userStore = useUserStore()
  if (userStore.getToken) {
    config.headers.Authorization = `Bearer ${userStore.getToken}`
  }
  return config
}, function (error) {
  return Promise.reject(error)
})

if (process.env.NODE_ENV != 'test') {
  api.interceptors.response.use(function (response) {
    return response.data
  }, async function (error) {
    const originalRequest = error.config
    
    if (error.response?.status == 401) {
      const errorData = error.response.data || {}
      // @fastify/jwt's onRequest-hook rejection (app.js) goes through Fastify's
      // default error serializer, not our own handleError -- its body shape is
      // { statusCode, code, error, message }, where `code` is the reliable
      // machine-readable JWT error and `error` is just the generic "Unauthorized"
      // HTTP status text (never JWT-specific). Check `code` first; keep the old
      // message-based checks only as a fallback for any other response shape.
      const errorMessage = errorData.error || errorData.message || ''

      // Handle JWT token expiration with automatic refresh
      if (errorData.code === 'FST_JWT_AUTHORIZATION_TOKEN_EXPIRED' ||
          errorMessage.includes('FST_JWT_AUTHORIZATION_TOKEN_EXPIRED') ||
          errorMessage.includes('JWT_EXPIRED') ||
          /token expired/i.test(errorMessage)) {
        
        if (!isRefreshing) {
          isRefreshing = true
          
          try {
            const userStore = useUserStore()
            if (userStore.loggedIn) {
              // Attempt to refresh the token
              const { token } = await api.post('/login/refresh', null, {
                headers: {
                  'x-api-key': import.meta.env.VITE_ADMIN_API_KEY,
                  'Content-Type': 'application/json',
                  Accept: 'application/json'
                }
              })
              
              api.defaults.headers = { Authorization: `Bearer ${token}` }
              userStore.setToken(token)
              
              // Retry the original request with the new token
              originalRequest.headers.Authorization = `Bearer ${token}`
              
              // Process any queued requests
              failedQueue.forEach(({ resolve }) => resolve())
              failedQueue = []
              
              return api(originalRequest)
            }
          } catch (refreshError) {
            // Refresh failed, logout the user
            failedQueue.forEach(({ reject }) => reject(refreshError))
            failedQueue = []
            event.emit('logout')
            return Promise.reject(normalizeApiError(refreshError))
          } finally {
            isRefreshing = false
          }
        } else {
          // If already refreshing, queue this request
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject })
          }).then(() => {
            return api(originalRequest)
          }).catch(err => {
            return Promise.reject(normalizeApiError(err))
          })
        }
      }
      
      // Don't logout for login errors - let the app handle them
      if (errorMessage !== 'Error Logging In') {
        event.emit('logout')
      }
    }
    
    return Promise.reject(normalizeApiError(error))
  })
}

export default {
  install: (app) => {
      const userStore = useUserStore()
      if (userStore.getToken) {
        console.log("Using cached auth token")
        api.defaults.headers = {'Authorization': `Bearer ${userStore.getToken}`}
      }
      app.config.globalProperties.$axios = api
  }
}
